import { ProductSlug } from "@/api/product-api";

/**
 * 기본 제품 → camo 변형 제품 매핑.
 *
 * camo 는 SKU 색상이 아니라 **별도 제품**이다(서버 데이터: `tactsuit-air` 의 `skuOptions` 에
 * camo 가 없고 `tactsuit-air-camo` 가 따로 있다). 그런데 **URL 은 기본 제품의 `?color=camo`
 * 로 통합**돼 있어서, 제품 slug ↔ URL 을 오갈 때 이 맵이 필요하다.
 *
 * ## 왜 공유 모듈인가 (#5386)
 *
 * 종전엔 `pages/shop/buy-tactsuit/[slug].tsx` 의 **지역 상수**였다. 그래서
 * `useInTheBoxProducts.ts` 가 *"지역 상수라 여기서 참조하지 않는다"* 고 적어 두고 우회했고,
 * GNB 모바일 메뉴는 camo 를 아예 판정하지 못해 **선택 표시가 안 떴다**(#5386 신고).
 *
 * 사본을 늘리면 갈린다 — camo 변형이 추가될 때 한쪽만 고쳐지는 클래스다. 그래서 **여기 하나**만
 * 둔다. 새 camo 변형이 생기면 이 맵에만 추가한다.
 */
export const CAMO_VARIANT_BY_SLUG: Partial<Record<string, ProductSlug>> = {
  [ProductSlug.TACTSUIT_AIR]: ProductSlug.TACTSUIT_AIR_CAMO,
  // 번들도 camo 가 별도 제품이다. 이 slug 로 페이지가 렌더되진 않지만
  // (`BUNDLE_BASE_BY_SLUG` 로 리다이렉트), **업그레이드로 선택됐을 때** 라인 안에서
  // camo 를 해석하려면 필요하다 — 기존 starter-bundle 페이지에도 Camo 스와치가 있었다.
  [ProductSlug.STARTER_BUNDLE]: ProductSlug.STARTER_BUNDLE_CAMO,
};

/** 기본 제품 slug → camo 변형 slug. 변형이 없으면 `undefined`. */
export const camoVariantOf = (slug: string): string | undefined =>
  CAMO_VARIANT_BY_SLUG[slug];

/**
 * camo 제품 slug → **라인 대표(비-camo) slug**. camo 가 아니면 그대로 돌려준다.
 *
 * URL 정규화 방향과 같다 — `tactsuit-air-camo` 는 `/shop/buy-tactsuit/tactsuit-air?color=camo`
 * 로 렌더되므로, 경로를 만들거나 대조할 때는 이 값을 쓴다.
 */
export const camoBaseOf = (slug: string): string =>
  Object.entries(CAMO_VARIANT_BY_SLUG).find(([, v]) => v === slug)?.[0] ?? slug;

/** 이 slug 자체가 camo 변형인가. */
export const isCamoSlug = (slug: string): boolean => camoBaseOf(slug) !== slug;
